/**
 * useCreateGames Hook
 * Date-based match loading with cache.
 * - Initial: fetch 10 days (today through today+9)
 * - When within 3 days of cached end: fetch 10 more
 * - When picking uncached date: fetch 3 before + 8 ahead
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { fetchGamesByDateRange } from '../helpers/createApi';
import {
  CREATE_FIXTURE_WINDOW_DAYS,
  INITIAL_DAYS_COUNT,
  CACHE_EXTEND_THRESHOLD,
  CACHE_EXTEND_BATCH,
  PICK_BUFFER_BEFORE,
  PICK_BUFFER_AFTER,
} from '../constants/create.constants';
import { logger } from '../../helpers/logger.helper';
import { isGameNotStarted } from '../helpers/createGames.helper';
import type { Game } from '../types/create.types';

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseDateStr(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

function getCachedRange(cache: Map<string, Game[]>): { min: string; max: string } | null {
  const keys = Array.from(cache.keys()).sort();
  if (keys.length === 0) return null;
  return { min: keys[0], max: keys[keys.length - 1] };
}

function daysBetween(fromStr: string, toStr: string): number {
  const from = parseDateStr(fromStr);
  const to = parseDateStr(toStr);
  return Math.round((to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000));
}

interface UseCreateGamesReturn {
  games: Game[];
  loading: boolean;
  filteredGames: Game[];
  gamesByLeague: Record<string, Game[]>;
}

/**
 * @param enabled - When false, no network requests are made (e.g. when user is on Arena, not Create tab).
 *                  Keeps initial fetch for when user later navigates to Create.
 */
/** Returns YYYY-MM-DD for (today at midnight + (days - 1)). */
function getWindowEndDateStr(today: Date, days: number): string {
  const d = new Date(today);
  d.setDate(d.getDate() + (days - 1));
  return toDateStr(d);
}

export const useCreateGames = (selectedDate: Date, enabled: boolean = true): UseCreateGamesReturn => {
  const [cache, setCache] = useState<Map<string, Game[]>>(() => new Map());
  const cacheRef = useRef(cache);
  cacheRef.current = cache;

  const [inFlightDates, setInFlightDates] = useState<Set<string>>(() => new Set());
  const [requestedRange, setRequestedRange] = useState<string[]>([]);
  const hasInitialFetchedRef = useRef(false);
  /** Stable "today" for the 14-day cap so the window does not shift if the user leaves the app open overnight. */
  const todayRef = useRef<Date | null>(null);

  const fetchRangeAndMerge = useCallback((dateStrs: string[]) => {
    if (dateStrs.length === 0) return;
    const missing = dateStrs.filter((d) => !cacheRef.current.has(d));
    if (missing.length === 0) return;

    missing.forEach((d) => setInFlightDates((prev) => new Set(prev).add(d)));
    const fromStr = dateStrs[0];
    const toStr = dateStrs[dateStrs.length - 1];

    fetchGamesByDateRange(fromStr, toStr)
      .then((byDate) => {
        setCache((prev) => {
          const next = new Map(prev);
          dateStrs.forEach((dateKey) => {
            next.set(dateKey, byDate.get(dateKey) ?? []);
          });
          return next;
        });
      })
      .finally(() => {
        setInFlightDates((prev) => {
          const next = new Set(prev);
          missing.forEach((d) => next.delete(d));
          return next;
        });
      });
  }, []);

  useEffect(() => {
    if (!enabled) return;

    if (todayRef.current === null) {
      const t = new Date();
      t.setHours(0, 0, 0, 0);
      todayRef.current = t;
    }
    const today = todayRef.current;
    const maxDateStr = getWindowEndDateStr(today, CREATE_FIXTURE_WINDOW_DAYS);

    const selectedStr = toDateStr(selectedDate);
    const todayStr = toDateStr(today);

    // Safety: never fetch outside the allowed window (even if UI tries to navigate there).
    if (selectedStr < todayStr || selectedStr > maxDateStr) return;

    if (!hasInitialFetchedRef.current) {
      hasInitialFetchedRef.current = true;
      const range: string[] = [];
      for (let i = 0; i < INITIAL_DAYS_COUNT; i++) {
        const d = new Date(today);
        d.setDate(d.getDate() + i);
        const str = toDateStr(d);
        if (str <= maxDateStr) range.push(str);
      }
      setRequestedRange(range);
      const missing = range.filter((d) => !cacheRef.current.has(d));
      if (missing.length > 0) fetchRangeAndMerge(range);
      return;
    }

    const cached = getCachedRange(cacheRef.current);
    const isInCache = cacheRef.current.has(selectedStr);

    if (isInCache && cached) {
      const distFromMax = daysBetween(selectedStr, cached.max);
      if (distFromMax <= CACHE_EXTEND_THRESHOLD) {
        const maxDate = parseDateStr(cached.max);
        const extendRange: string[] = [];
        for (let i = 1; i <= CACHE_EXTEND_BATCH; i++) {
          const d = new Date(maxDate);
          d.setDate(d.getDate() + i);
          const str = toDateStr(d);
          if (str <= maxDateStr) extendRange.push(str);
        }
        const missing = extendRange.filter((d) => !cacheRef.current.has(d));
        if (missing.length > 0) fetchRangeAndMerge(extendRange);
      }
      return;
    }

    if (!isInCache) {
      const selected = parseDateStr(selectedStr);
      const range: string[] = [];
      for (let i = -PICK_BUFFER_BEFORE; i <= PICK_BUFFER_AFTER; i++) {
        const d = new Date(selected);
        d.setDate(d.getDate() + i);
        if (d >= today) {
          const str = toDateStr(d);
          if (str <= maxDateStr) range.push(str);
        }
      }
      if (range.length === 0) range.push(selectedStr);
      setRequestedRange(range);
      const missing = range.filter((d) => !cacheRef.current.has(d));
      if (missing.length > 0) fetchRangeAndMerge(range);
    }
  }, [enabled, selectedDate, fetchRangeAndMerge]);

  const selectedDateStr = selectedDate ? toDateStr(selectedDate) : '';

  const loading = useMemo(() => {
    if (!selectedDateStr) return false;
    if (!requestedRange.includes(selectedDateStr)) return false;
    if (cache.has(selectedDateStr)) return false;
    return inFlightDates.has(selectedDateStr);
  }, [selectedDateStr, requestedRange, cache, inFlightDates]);

  const games = useMemo(() => {
    const list: Game[] = [];
    const keys = Array.from(cache.keys()).sort();
    keys.forEach((key) => list.push(...(cache.get(key) ?? [])));
    return list;
  }, [cache]);

  const filteredGames = useMemo(() => {
    if (!selectedDateStr) return [];
    const dayGames = cache.get(selectedDateStr) ?? [];
    return dayGames.filter(
      (g) => isGameNotStarted(g) && g.hasRealOdds !== false
    );
  }, [cache, selectedDateStr]);

  useEffect(() => {
    if (!selectedDateStr) return;
    const dayGames = cache.get(selectedDateStr) ?? [];
    const hiddenNoOdds = dayGames.filter(
      (g) => isGameNotStarted(g) && g.hasRealOdds === false
    );
    for (const g of hiddenNoOdds) {
      logger.warn('[useCreateGames] Match hidden from create list (no odds row)', {
        matchId: g.id,
        date: g.date,
        kickoff: g.time,
        league: g.league,
        homeTeam: g.homeTeam,
        awayTeam: g.awayTeam,
      });
    }
  }, [cache, selectedDateStr]);

  const gamesByLeague = useMemo(() => {
    return filteredGames.reduce((acc, game) => {
      if (!acc[game.league]) acc[game.league] = [];
      acc[game.league].push(game);
      return acc;
    }, {} as Record<string, Game[]>);
  }, [filteredGames]);

  return {
    games,
    loading,
    filteredGames,
    gamesByLeague,
  };
};
